import React, { useState, useEffect } from 'react';
import type { LayerStyleProperties } from '../../store/types';

import { TextInput } from '../inputs/TextInput';
import { NumberInput } from '../inputs/NumberInput';
import { CheckboxInput } from '../inputs/CheckboxInput';
import { SelectInput, type SelectOption as AtomicSelectOption } from '../inputs/SelectInput';
import { ColorInput } from '../inputs/ColorInput';
import { RangeSlider } from '../inputs/RangeSlider';
import { ExpressionTextarea } from '../inputs/ExpressionTextarea';

export type StyleValue = string | number | boolean | any[] | null | undefined;

interface NumberConfig {
  min?: number;
  max?: number;
  step?: number;
}

interface SelectOption {
  value: string | number;
  label: string;
}

interface StylePropertyInputProps {
  layerId: string;
  propertyName: keyof LayerStyleProperties;
  label: string;
  currentValue: StyleValue;
  propertyType: 'color' | 'number' | 'range' | 'boolean' | 'select' | 'text' | string;
  isExpressionPossible: boolean;
  availablePropertyKeys?: string[];
  onStyleChange: (layerId: string, propertyName: keyof LayerStyleProperties, value: StyleValue) => void;
  numberConfig?: NumberConfig;
  selectOptions?: SelectOption[];
  className?: string;
  disabled?: boolean;
  tooltip?: string;
  hideNumericValueDisplay?: boolean;
}

const StylePropertyInput: React.FC<StylePropertyInputProps> = ({
  layerId,
  propertyName,
  label: mainLabel,
  currentValue,
  propertyType,
  isExpressionPossible,
  availablePropertyKeys = [],
  onStyleChange,
  numberConfig = {},
  selectOptions = [],
  className = '',
  disabled = false,
  tooltip,
}) => {
  const isInitiallyExpression = Array.isArray(currentValue);
  const [isExpressionMode, setIsExpressionMode] = useState<boolean>(isInitiallyExpression);
  const [localValue, setLocalValue] = useState<StyleValue>(currentValue);
  const [expressionInput, setExpressionInput] = useState<string>(
    isInitiallyExpression ? JSON.stringify(currentValue, null, 2) : ''
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [showAvailableKeys, setShowAvailableKeys] = useState<boolean>(false);

  useEffect(() => {
    setLocalValue(currentValue);
    if (!Array.isArray(currentValue)) {
      setParseError(null);
    }
    const isExternalValueExpression = Array.isArray(currentValue);
    setIsExpressionMode(isExternalValueExpression);
    if (isExternalValueExpression) {
      setExpressionInput(JSON.stringify(currentValue, null, 2));
    }
     
  }, [currentValue, layerId, propertyName]);

  useEffect(() => {
    if (!isExpressionMode && Array.isArray(currentValue)) {
      let defaultValue: StyleValue;
      switch (propertyType) {
        case 'color': defaultValue = '#000000'; break;
        case 'number':
        case 'range': defaultValue = numberConfig?.min ?? 0; break;
        case 'boolean': defaultValue = false; break;
        case 'select': defaultValue = selectOptions.length > 0 ? selectOptions[0].value : ''; break;
        default: defaultValue = '';
      }
      onStyleChange(layerId, propertyName as keyof LayerStyleProperties, defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpressionMode, layerId, propertyName, propertyType, numberConfig, selectOptions, onStyleChange]);

  const handleToggleExpressionMode = () => {
    const nextIsExpressionMode = !isExpressionMode;
    setIsExpressionMode(nextIsExpressionMode);
    if (nextIsExpressionMode) { 
        // Entrando in modalità espressione, popola con il valore corrente (se array) o stringa vuota
        setExpressionInput(Array.isArray(localValue) ? JSON.stringify(localValue, null, 2) : '');
        setParseError(null); // Pulisci errori precedenti
    } else {
        // Uscendo dalla modalità espressione, onStyleChange sarà triggerato dall'useEffect sopra
        // per resettare al valore di default se necessario.
        // localValue dovrebbe già essere sincronizzato con lo store.
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement> | { target: { value: string; name?: string | undefined; } }) => {
    const newValue = event.target.value;
    onStyleChange(layerId, propertyName as keyof LayerStyleProperties, newValue);
    setLocalValue(newValue);
  };

  // NumberInput si aspetta (value: number | string) => void
  const handleNumberChange = (valueFromInput: number | string) => {
    let processedValue: number | undefined;

    if (typeof valueFromInput === 'string') {
        if (valueFromInput.trim() === '') {
            processedValue = undefined; // Stringa vuota diventa undefined
        } else {
            const num = parseFloat(valueFromInput);
            processedValue = isNaN(num) ? undefined : num; // Stringa non numerica diventa undefined
        }
    } else { // è un number
        processedValue = valueFromInput;
    }
    
    setLocalValue(valueFromInput); // Mantiene l'input grezzo per il feedback UI immediato

    if (processedValue !== undefined) {
        let finalValue = processedValue;
        if (numberConfig?.min !== undefined && finalValue < numberConfig.min) finalValue = numberConfig.min;
        if (numberConfig?.max !== undefined && finalValue > numberConfig.max) finalValue = numberConfig.max;
        onStyleChange(layerId, propertyName as keyof LayerStyleProperties, finalValue);
        // setLocalValue(finalValue); // Aggiorna localValue con il valore clampato/processato solo se valido
                                  // In realtà, è meglio che localValue rifletta l'input dell'utente
                                  // e lo store il valore valido.
                                  // Ma per coerenza con gli altri, aggiorniamo localValue con il valore valido.
                                  // O meglio, lasciamo che sia lo store a pilotare localValue tramite useEffect [currentValue]
    } else {
        onStyleChange(layerId, propertyName as keyof LayerStyleProperties, undefined); // Invia undefined se l'input non è un numero valido
    }
  };
  
  const handleBooleanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    onStyleChange(layerId, propertyName as keyof LayerStyleProperties, newValue);
    setLocalValue(newValue);
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    // Considera se il valore deve essere convertito a numero se le opzioni sono numeriche
    // Per ora, lo tratta come stringa o numero come viene.
    onStyleChange(layerId, propertyName as keyof LayerStyleProperties, newValue);
    setLocalValue(newValue);
  };
  
  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onStyleChange(layerId, propertyName as keyof LayerStyleProperties, newValue);
    setLocalValue(newValue);
  };

  const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value);
    onStyleChange(layerId, propertyName as keyof LayerStyleProperties, newValue);
    setLocalValue(newValue);
  };

  // ExpressionTextarea si aspetta (value: string) => void
  const handleExpressionStringChange = (newExpressionString: string) => {
    setExpressionInput(newExpressionString);
    setParseError(null); // Rimuovi l'errore mentre si digita
    // Il parsing e l'aggiornamento allo store avverranno al "blur" o con azione esplicita
  };

  // Questa funzione è chiamata quando l'utente vuole applicare l'espressione
  // (es. al blur del textarea, o tramite un bottone "Applica")
  // Poiché ExpressionTextarea non ha onBlur, questo deve essere gestito esternamente.
  // La funzione applyExpression è stata rimossa perché non utilizzata (TS6133)
  // Se in futuro si aggiungerà un bottone "Applica Espressione", questa logica andrà ripristinata
  // e collegata all'evento onClick del bottone.
  // Esempio di utilizzo precedente:
  // const applyExpression = () => {
  //   if (expressionInput.trim() === '') {
  //     setParseError(null);
  //     onStyleChange(layerId, propertyName as keyof LayerStyleProperties, undefined);
  //     return;
  //   }
  //   try {
  //     const parsedExpression = JSON.parse(expressionInput);
  //     if (Array.isArray(parsedExpression)) {
  //       setParseError(null);
  //       onStyleChange(layerId, propertyName as keyof LayerStyleProperties, parsedExpression as any[]);
  //     } else {
  //       setParseError('L\'espressione deve essere un array JSON valido.');
  //     }
  //   } catch (error) {
  //     setParseError('Errore nel formato JSON. Controlla la sintassi.');
  //   }
  // };

  const commonInputPropsBase = {
    id: `${layerId}-${propertyName}`,
    disabled: disabled || isExpressionMode,
  };

  const renderSimpleInput = () => {
    switch (propertyType) {
      case 'color':
        return (
          <ColorInput
            {...commonInputPropsBase}
            value={typeof localValue === 'string' ? localValue : '#000000'}
            onChange={handleColorChange}
          />
        );
      case 'number':
        return (
          <NumberInput
            {...commonInputPropsBase}
            // NumberInput gestisce internamente la conversione da stringa a numero e viceversa per il display.
            // Passiamo il valore che abbiamo, NumberInput dovrebbe gestirlo.
            // Se localValue è undefined (es. dopo input invalido), passiamo stringa vuota.
            value={localValue === undefined ? '' : (typeof localValue === 'number' ? localValue : String(localValue))}
            onChange={handleNumberChange} // Questo handler riceve (value: number | string)
            min={numberConfig?.min}
            max={numberConfig?.max}
            step={numberConfig?.step}
            placeholder={String(numberConfig?.min ?? 0)}
          />
        );
      case 'range':
        return (
          <RangeSlider
            {...commonInputPropsBase}
            value={typeof localValue === 'number' ? localValue : (numberConfig?.min ?? 0)}
            onChange={handleRangeChange}
            min={numberConfig?.min ?? 0}
            max={numberConfig?.max ?? 100}
            step={numberConfig?.step ?? 1}
            // hideNumericValueDisplay prop rimosso perché non supportato da RangeSlider
          />
        );
      case 'boolean':
        return (
          <CheckboxInput
            {...commonInputPropsBase}
            checked={!!localValue}
            onChange={handleBooleanChange}
          />
        );
      case 'select': {
        const atomicOptions: AtomicSelectOption[] = selectOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
        }));
        return (
          <SelectInput
            {...commonInputPropsBase}
            value={typeof localValue === 'string' || typeof localValue === 'number' ? localValue : ''}
            onChange={handleSelectChange}
            options={atomicOptions}
          />
        );
      }
      case 'text':
      case 'string':
      default:
        return (
          <TextInput
            {...commonInputPropsBase}
            value={String(localValue ?? '')}
            onChange={handleTextChange}
            placeholder={`Valore per ${mainLabel}`}
          />
        );
    }
  };

  const labelId = `${layerId}-${propertyName}-label`;

  return (
    <div className={`style-control ${propertyType === 'boolean' ? 'style-control-checkbox' : ''} ${className}`}>
      <label htmlFor={commonInputPropsBase.id} id={labelId} title={tooltip} className="style-label">
        {mainLabel}
        {isExpressionPossible && (
          <button
            type="button"
            onClick={handleToggleExpressionMode}
            disabled={disabled}
            className={`expression-toggle-btn ${isExpressionMode ? 'active' : ''}`}
            title={isExpressionMode ? 'Passa a modalità semplice' : 'Passa a modalità espressione'}
            aria-pressed={isExpressionMode}
          >
            E
          </button>
        )}
      </label>

      <div className="style-control-input-wrapper">
        {isExpressionMode ? (
          <div className="expression-input-container">
            <ExpressionTextarea
              id={`${layerId}-${propertyName}-expression`}
              value={expressionInput}
              onChange={handleExpressionStringChange} // Riceve (value: string)
              // onBlur non è supportato da ExpressionTextarea.
              // L'utente dovrà usare applyExpression() tramite un altro meccanismo (es. bottone)
              disabled={disabled}
              rows={4}
              placeholder='Inserisci espressione MapLibre JSON, es. ["get", "propertyName"]'
              aria-label={`Espressione per ${mainLabel}`}
              aria-invalid={!!parseError}
              aria-describedby={parseError ? `${layerId}-${propertyName}-error` : undefined}
            />
            {/* Potenziale bottone per applicare l'espressione, se onBlur non è fattibile */}
            {/* <button onClick={applyExpression} disabled={disabled || !!parseError}>Applica Espressione</button> */}
            {parseError && (
              <p id={`${layerId}-${propertyName}-error`} className="expression-error" role="alert">
                {parseError}
              </p>
            )}
            {availablePropertyKeys.length > 0 && (
              <div className="available-properties">
                <button
                  type="button"
                  onClick={() => setShowAvailableKeys(!showAvailableKeys)}
                  className="toggle-keys-button"
                  aria-expanded={showAvailableKeys}
                >
                  {showAvailableKeys ? 'Nascondi' : 'Mostra'} chiavi proprietà disponibili
                </button>
                {showAvailableKeys && (
                  <ul>
                    {availablePropertyKeys.map((key) => (
                      <li key={key}><code>{key}</code></li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : (
          renderSimpleInput()
        )}
      </div>
    </div>
  );
};

export default StylePropertyInput;